import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { filter } from 'lodash'
import { useTeam } from '../../contexts/TeamContext'
import useNodesResources from '../../shared/resources/api/v1/admin/useNodesResources'
import LoadingState from '../../shared/components/LoadingState'
import ErrorState from '../../shared/components/ErrorState'
import Page from '../../shared/components/layouts/Page'
import PageHeader from '../../shared/components/layouts/PageHeader'
import PageBody from '../../shared/components/layouts/PageBody'
import CodeBlock from '../../shared/components/CodeBlock'
import FlexColumn from '../../shared/components/layouts/FlexColumn'
import Flex from '../../shared/components/layouts/Flex'
import UnconfirmedNodes from './components/UnconfirmedNodes'

function NodeCreatePage() {
  const { teamSlug } = useParams()
  const { team, loading: teamLoading } = useTeam()
  const { all, confirm } = useNodesResources(teamSlug)
  const { enqueueSnackbar } = useSnackbar()
  const { formatMessage } = useIntl()

  const [nodes, setNodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    all()
      .then((response) => {
        setNodes(response.nodes)
      })
      .catch((err) => {
        setError(err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [teamSlug])

  const unconfirmedNodes = useMemo(() => {
    return filter(nodes, (node) => !node.confirmed)
  }, [nodes])

  const handleCopyCommand = useCallback((command) => {
    navigator.clipboard.writeText(command)
      .then(() => {
        enqueueSnackbar(formatMessage({ id: 'nodes.create.copySuccess' }), { variant: 'success' })
      })
      .catch(() => {
        enqueueSnackbar(formatMessage({ id: 'nodes.create.copyError' }), { variant: 'error' })
      })
  }, [enqueueSnackbar, formatMessage])

  const handleConfirmNode = useCallback((node) => {
    confirm(node.id)
      .then((response) => {
        setNodes((prevNodes) =>
          prevNodes.map((n) => (n.id === node.id ? response.node : n))
        )
        enqueueSnackbar(formatMessage({ id: 'nodes.create.unconfirmedNodes.confirmSuccess' }), { variant: 'success' })
      })
      .catch(() => {
        enqueueSnackbar(formatMessage({ id: 'nodes.create.unconfirmedNodes.confirmError' }), { variant: 'error' })
      })
  }, [confirm, enqueueSnackbar, formatMessage])

  const installerCommands = useMemo(() => {
    if (!team?.installerUrl) return ''
    return `wget ${team.installerUrl} -O installer.sh
chmod +x installer.sh
./installer.sh`
  }, [team?.installerUrl])

  if (loading || teamLoading) return <LoadingState />
  if (error) return <ErrorState messageId="nodes.error" />

  return (
    <Page>
      <PageHeader
        title={<FormattedMessage id="nodes.create.title" />}
        entities={{ team }}
      />
      <PageBody>
        <FlexColumn gap={4}>
          <FlexColumn gap={2}>
            <Typography variant="h3">
              <FormattedMessage id="nodes.create.instructions.title" />
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <FormattedMessage id="nodes.create.instructions.description" />
            </Typography>

            <FlexColumn gap={1}>
              <Typography variant="h4">
                <FormattedMessage id="nodes.create.step1.title" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="nodes.create.step1.description" />
              </Typography>
            </FlexColumn>

            <FlexColumn gap={1}>
              <Typography variant="h4">
                <FormattedMessage id="nodes.create.step2.title" />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="nodes.create.step2.description" />
              </Typography>

              <Flex alignItems="flex-start" gap={1}>
                <CodeBlock sx={{ flex: 1 }}>
                  {installerCommands}
                </CodeBlock>
                <Tooltip title={<FormattedMessage id="nodes.create.copyTooltip" />}>
                  <IconButton
                    onClick={() => handleCopyCommand(installerCommands)}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Flex>
            </FlexColumn>
          </FlexColumn>

          <UnconfirmedNodes nodes={unconfirmedNodes} teamSlug={teamSlug} onConfirm={handleConfirmNode} />
        </FlexColumn>
      </PageBody>
    </Page>
  )
}

export default NodeCreatePage
